import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaGift, FaQrcode, FaStore, FaShieldAlt, FaArrowRight, FaCheckCircle, FaExternalLinkAlt } from 'react-icons/fa';
import ExternalForfeoRedirectModal from '../components/ExternalForfeoRedirectModal';
import { FORFEO_GIFT_CARD_URL } from '../lib/forfeoExternalLinks';

const copy = {
  fr: {
    badge: 'Carte Accès Privilège Forfeo', title: 'Une carte, des centaines de possibilités locales.', subtitle: 'La Carte Accès Privilège Forfeo est la carte numérique officielle de Forfeo : un cadeau local, flexible et utilisable dans le réseau de partenaires Forfeo selon les modalités applicables.',
    cta: 'Acheter sur forfeo.com', secondary: 'Comment ça marche',
    features: [
      { icon: 'gift', title: 'Cadeau flexible', text: 'Offrez une carte numérique qui laisse le destinataire choisir son expérience.' },
      { icon: 'store', title: 'Réseau local', text: 'Utilisable chez les commerçants et partenaires membres du réseau Forfeo selon les modalités applicables.' },
      { icon: 'qr', title: 'Validation simple', text: 'La carte peut être validée par QR code ou confirmation numérique chez le partenaire.' },
      { icon: 'shield', title: 'Suivi sécurisé', text: 'Les confirmations, soldes et validations sont pensés pour réduire les erreurs et simplifier l’expérience.' },
    ],
    noteTitle: 'Important à savoir', note: 'La disponibilité, les conditions d’utilisation et les partenaires admissibles peuvent varier selon les offres actives et les règles du réseau Forfeo.',
    finalTitle: 'Un cadeau local qui ouvre plusieurs possibilités.', bridge: 'L’achat de la carte virtuelle Forfeo se fait sur forfeo.com, la vitrine transactionnelle officielle de Forfeo. Forfeo Explore reste votre espace pour découvrir quoi offrir et quoi vivre.'
  },
  en: {
    badge: 'Forfeo Privilege Access Card', title: 'One card, hundreds of local possibilities.', subtitle: 'The Forfeo Privilege Access Card is Forfeo’s official digital card: a local, flexible gift usable across the Forfeo partner network according to applicable terms.',
    cta: 'Buy on forfeo.com', secondary: 'How it works',
    features: [
      { icon: 'gift', title: 'Flexible gift', text: 'Offer a digital card that lets the recipient choose their experience.' },
      { icon: 'store', title: 'Local network', text: 'Usable with merchants and partners in the Forfeo network according to applicable terms.' },
      { icon: 'qr', title: 'Simple validation', text: 'The card can be validated by QR code or digital confirmation with the partner.' },
      { icon: 'shield', title: 'Secure tracking', text: 'Confirmations, balances and validations are designed to reduce errors and simplify the experience.' },
    ],
    noteTitle: 'Important to know', note: 'Availability, usage conditions and eligible partners may vary depending on active offers and Forfeo network rules.',
    finalTitle: 'A local gift that opens many possibilities.', bridge: 'The Forfeo virtual card is purchased on forfeo.com, Forfeo’s official transactional storefront. Forfeo Explore remains your space to discover what to offer and what to experience.'
  }
};

const icons: Record<string, React.ReactNode> = { gift: <FaGift />, store: <FaStore />, qr: <FaQrcode />, shield: <FaShieldAlt /> };

export default function CarteCadeauUniverselle() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const t = copy[lang];
  const [isRedirectOpen, setIsRedirectOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">
      <Header />
      <ExternalForfeoRedirectModal isOpen={isRedirectOpen} onClose={() => setIsRedirectOpen(false)} targetUrl={FORFEO_GIFT_CARD_URL} contextLabel="forfeo.com/carte-cadeau" />
      <main className="pt-28 pb-24">
        <section className="px-4 py-20 bg-[radial-gradient(circle_at_top_left,#fef3c7,transparent_32%),radial-gradient(circle_at_top_right,#dbeafe,transparent_35%)]">
          <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white border border-slate-200 px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest mb-8"><FaGift className="text-orange-500" /> {t.badge}</div>
              <h1 className="text-5xl md:text-7xl font-black tracking-tight mb-6">{t.title}</h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-10">{t.subtitle}</p>
              <div className="flex flex-col sm:flex-row gap-4">
                <button type="button" onClick={() => setIsRedirectOpen(true)} className="px-8 py-4 bg-slate-900 text-white rounded-2xl font-black hover:bg-slate-800 transition flex items-center justify-center gap-3">{t.cta} <FaExternalLinkAlt /></button>
                <Link to="/comment-ca-marche" className="px-8 py-4 bg-white border border-slate-200 text-slate-900 rounded-2xl font-black hover:bg-slate-50 transition">{t.secondary}</Link>
              </div>
            </div>
            <div className="relative">
              <div className="relative group">
                <div className="absolute -inset-4 rounded-[44px] bg-gradient-to-br from-cyan-400/40 via-blue-600/30 to-amber-300/40 blur-2xl opacity-80 group-hover:opacity-100 transition" />
                <div className="relative overflow-hidden rounded-[38px] p-8 text-white shadow-2xl rotate-2 hover:rotate-0 transition duration-500 bg-[#050816] border border-white/10">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_12%,rgba(34,211,238,0.32),transparent_28%),radial-gradient(circle_at_85%_18%,rgba(245,158,11,0.22),transparent_26%),linear-gradient(135deg,rgba(255,255,255,0.08),transparent_42%)]" />
                  <div className="absolute -right-14 -bottom-14 h-44 w-44 rounded-full border border-cyan-300/20" />
                  <div className="absolute right-8 top-8 grid grid-cols-2 gap-1 opacity-90">
                    {[0, 1, 2, 3].map((dot) => (
                      <span key={dot} className="h-2.5 w-2.5 rounded-[3px] bg-cyan-300 shadow-[0_0_16px_rgba(103,232,249,0.9)]" />
                    ))}
                  </div>
                  <div className="relative flex items-center justify-between mb-14">
                    <span className="font-black tracking-[0.32em] text-sm">FORFEO</span>
                    <FaQrcode className="text-cyan-300 text-3xl" />
                  </div>
                  <div className="relative text-xs uppercase tracking-[0.34em] text-cyan-300 mb-3">Carte Accès Privilège</div>
                  <div className="relative text-4xl md:text-5xl font-black mb-2">Forfeo</div>
                  <div className="relative text-xs uppercase tracking-[0.28em] text-white/55 mb-8">Réseau local numérique</div>
                  <div className="relative h-12 rounded-2xl bg-white/10 border border-white/10 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 w-2/3 bg-gradient-to-r from-cyan-300/25 to-transparent" />
                  </div>
                  <div className="relative mt-6 flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-white/55">
                    <span>Valide réseau Forfeo</span>
                    <span>QC</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-10">
          <div className="rounded-[32px] border border-blue-100 bg-blue-50 p-8 md:p-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[10px] font-black uppercase tracking-widest text-blue-700 mb-4"><FaShieldAlt /> forfeo.com</div>
            <p className="text-slate-700 font-semibold leading-relaxed">{t.bridge}</p>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.features.map((feature) => (
              <div key={feature.title} className="bg-white border border-slate-100 rounded-3xl p-7 shadow-xl shadow-slate-200/40">
                <div className="w-14 h-14 rounded-2xl bg-orange-50 text-orange-600 flex items-center justify-center text-xl mb-5">{icons[feature.icon]}</div>
                <h3 className="text-xl font-black mb-3">{feature.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{feature.text}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-10">
          <div className="bg-slate-50 border border-slate-100 rounded-[32px] p-8 md:p-10">
            <h2 className="text-3xl font-black mb-4 flex items-center gap-3"><FaCheckCircle className="text-green-500" /> {t.noteTitle}</h2>
            <p className="text-slate-600 leading-relaxed">{t.note}</p>
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 text-center py-20">
          <h2 className="text-3xl md:text-4xl font-black mb-8">{t.finalTitle}</h2>
          <button type="button" onClick={() => setIsRedirectOpen(true)} className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-2xl font-black hover:bg-blue-700 transition">{t.cta} <FaExternalLinkAlt /></button>
        </section>
      </main>
      <Footer />
    </div>
  );
}
